import React, { useState, useEffect } from 'react';
import { Box, Dialog, DialogContent, DialogTitle, IconButton, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { SmartFilter } from '../../components/common/SmartFilter';
import Pagination from '../../components/atoms/Pagination';
import { useAuth } from '../../store/AuthContext';
import { CreateTableHeader } from '../../components/common/TableHeader';

import { debounce } from '../../utils/helper';
import { ICoupon } from '../../utils/entities/interface';
import { GETCoupons } from '../../services/coupen';
import { headerCouponTable } from '../../utils/constants';
import { CouponList } from '../../components/ui/list/Coupons';
import withComponentAuth from '../../hoc/withComponentAuth';
import CreateCouponForm from '../../components/ui/add/coupon';

const CouponManagement = () => {
  const [page, setPage] = useState(1); // Starting from page 1
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [coupons, setCoupons] = useState<ICoupon[]>([]);
  const [totalCoupons, setTotalCoupons] = useState(0);
  const [searchQuery, setSearchQuery] = useState<string | undefined>(undefined);
  const [sort, setSort] = useState<'ASC' | 'DESC'>('ASC');
  const [isLoading, setIsLoading] = useState(true);
  const [editingCoupon, setEditingCoupon] = useState<ICoupon | null>(null);

  // Fetch coupons whenever page, rowsPerPage, or searchQuery changes
  useEffect(() => {
    fetchCoupons(page, rowsPerPage, searchQuery, sort);
  }, [page, rowsPerPage, searchQuery, sort]);

  // Fetch coupons from API
  const fetchCoupons = async (
    page: number,
    limit: number,
    searchQuery?: string,
    sort?: 'ASC' | 'DESC',
  ) => {
    try {
      setIsLoading(true);
      const response = await GETCoupons({
        page,
        limit,
        searchQuery,
        sortOrder: sort,
      });
      console.log('response', response);
      setCoupons(response.data); // Set the coupon data from the API
      setTotalCoupons(response.count); // Set the total number of coupons
    } catch (error) {
      console.error('Failed to fetch coupons:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleChangePage = (event: any, newPage: number) => {
    setPage(newPage + 1); // API expects 1-based page index
  };

  const handleChangeRowsPerPage = (event: any) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(1); // Reset to the first page when rows per page changes
  };

  const refreshList = () => {
    fetchCoupons(page, rowsPerPage, searchQuery);
  };

  const handleSaveRefresh = () => {
    refreshList();
  };

  // Debounced search handler
  const handleSearchRefresh = debounce((search: string) => {
    setSearchQuery(search); // Update the search query state
    setPage(1); // Reset to the first page for new search
  }, 500); // 500ms delay before search executes

  const handleFilterRefresh = () => {
    console.log('FILTER HAPPENED');
  };

  const handleSortRefresh = () => {
    setSort(sort === 'ASC' ? 'DESC' : 'ASC');
    refreshList();
  };

  const handleEditCoupon = (coupon: ICoupon) => {
    setEditingCoupon(coupon);
  };

  const closeEditDialog = () => {
    setEditingCoupon(null);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        maxHeight: '100%',
      }}
    >
      <SmartFilter
        title="Coupons"
        createFilter={['SEARCH', 'ADD']}
        addElement={<CreateCouponForm />}
        filterHeader={'Filter Coupons'}
        addHeader={'Add Coupon'}
        onSaved={handleSaveRefresh}
        onFiltered={handleFilterRefresh}
        onSearch={handleSearchRefresh}
        onSort={handleSortRefresh}
        addDrawerSize="md"
      />
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          minHeight: 0,
          p: 1,
        }}
      >
        <Box
          sx={{
            height: '100%',
            maxHeight: '100%',
            overflowY: 'scroll',
            scrollbarWidth: 'none',
          }}
        >
          {coupons.length <= 0 && !isLoading ? (
            <Typography textAlign={'center'} marginTop={'20%'}>
              No Coupons Found
            </Typography>
          ) : (
            <CouponList
              headerCells={headerCouponTable}
              coupons={coupons}
              onUpdate={refreshList}
              isLoading={isLoading}
              onEdit={handleEditCoupon}
            />
          )}
        </Box>

        <Box display={'block'} bottom={0} right={0}>
          <Pagination
            count={totalCoupons} // Use totalCoupons from API response
            page={page - 1} // Convert 1-based to 0-based page index for the pagination component
            rowsPerPage={rowsPerPage}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Box>
      </Box>

      <Dialog
        open={Boolean(editingCoupon)}
        onClose={closeEditDialog}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Edit Coupon
          <IconButton onClick={closeEditDialog}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {editingCoupon && (
            <CreateCouponForm
              editingCoupon={editingCoupon}
              onSaved={refreshList}
              handleDrawerClose={closeEditDialog}
            />
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default withComponentAuth(CouponManagement);
